/**
 * **Prediction vs execution**
 * - JS here interprets an `EnforcementPlan` that was already computed by `evaluateLockbox` (pure rules).
 *   That is **prediction / observability** (sandbox labels, traces, logs).
 * - **Execution** is native: FamilyControls / ManagedSettings / tokens — may differ by OS policy, picker
 *   state, or delays. Never treat this file as identical to on-device shields.
 *
 * Dual-mode pipeline: same EnforcementPlan → sandbox interpretation (no Apple APIs) or
 * documented production hand-off (native adapter). Does **not** call Swift.
 */

import { buildDecisionTrace } from "../domain/decisionTrace.js";
import { defaultClassification } from "../domain/types.js";

/** @typedef {import('./types.js').SimulatedEnforcementResult} SimulatedEnforcementResult */
/** @typedef {import('./types.js').EnforcementRuntimeMode} EnforcementRuntimeMode */
/** @typedef {import('../domain/types.js').EnforcementPlan} EnforcementPlan */

/**
 * @typedef {Object} SimulateOptions
 * @property {EnforcementRuntimeMode} [enforcementMode] default "sandbox"
 * @property {(lockboxAppId: string) => string} [labelResolver] map id → display label
 * @property {'none'|'blocked_wins'} [allowBlockConflictResolution] for synthetic conflict tests
 * @property {boolean} [skipProductionNote] default false
 * @property {boolean} [includeDecisionTraces] when true, require `evaluationContext`
 * @property {{ rulepack: import('../domain/types.js').LockboxRulepack, world: import('../domain/types.js').EvaluationWorld }} [evaluationContext]
 */

/**
 * Fake “Swift layer”: interprets an already-evaluated EnforcementPlan without native I/O.
 *
 * @param {EnforcementPlan} plan
 * @param {SimulateOptions} [options]
 * @returns {SimulatedEnforcementResult}
 */
export function simulateEnforcement(plan, options = {}) {
  const enforcementMode = options.enforcementMode ?? "sandbox";
  const labelResolver = options.labelResolver ?? ((id) => id);
  const conflict = options.allowBlockConflictResolution ?? "none";

  let blocked = [...(plan.blockAppIds || [])];
  let allowed = [...(plan.allowAppIds || [])];

  /** @type {string[]} */
  const explanation = [];

  if (enforcementMode === "sandbox") {
    explanation.push("Sandbox: simulated shields only — no ManagedSettingsStore / DeviceActivity / FamilyControls.");
  } else {
    explanation.push(
      "Production: native `submitRestrictionIntent` would map enforceable targets → ApplicationToken (not run here).",
    );
  }

  if (conflict === "blocked_wins") {
    const bset = new Set(blocked);
    const before = allowed.length;
    allowed = allowed.filter((id) => !bset.has(id));
    if (before !== allowed.length) {
      explanation.push("Conflict resolution (sandbox test): same id in block + allow → treated as blocked.");
    }
  }

  /** @type {EnforcementPlan} */
  const planForTrace = {
    ...plan,
    blockAppIds: [...blocked].sort(),
    allowAppIds: [...allowed].sort(),
  };

  const triggeredRules = (plan.matchedRules || []).map((r) => r.ruleId);
  for (const r of plan.matchedRules || []) {
    explanation.push(`${r.ruleId}${r.appId ? ` [${r.appId}]` : ""}: ${r.reason}`);
  }

  /** @type {SimulatedEnforcementResult} */
  const result = {
    blocked: blocked.map(labelResolver),
    allowed: allowed.map(labelResolver),
    triggeredRules,
    modeApplied: plan.activeModeId,
    explanation,
    enforcementMode,
  };

  if (options.includeDecisionTraces && options.evaluationContext) {
    const { rulepack, world } = options.evaluationContext;
    const byId = new Map((world.apps || []).map((a) => [a.id, a]));
    const ids = [...new Set([...planForTrace.blockAppIds, ...planForTrace.allowAppIds])];
    result.decisionTraces = ids.map((id) => {
      const row =
        byId.get(id) ||
        /** @type {import('../domain/types.js').LockboxAppEntity} */ ({
          id,
          displayName: id,
          classification: defaultClassification("unknown"),
          nativeRef: null,
        });
      return buildDecisionTrace(row, rulepack, world, planForTrace);
    });
    result.decisionTraces.sort((a, b) => a.app.localeCompare(b.app));
  }

  return result;
}
